import { PropTypes } from "prop-types";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import Overlay from "react-bootstrap/Overlay";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

const HIDE_DELAY_MS = 150;
const DEFAULT_POPPER_CONFIG = {
  strategy: "fixed",
  modifiers: [
    {
      name: "flip",
      options: {
        fallbackPlacements: ["left", "bottom", "top"],
      },
    },
    {
      name: "preventOverflow",
      options: {
        boundary: "viewport",
        padding: 8,
      },
    },
  ],
};

const Tooltipable = ({
  text = null,
  trigger = "hover",
  placement = "bottom",
  className = "d-inline-block",
  innerClassName = "",
  tooltipContentClassName = "",
  flip = true,
  popperConfig = DEFAULT_POPPER_CONFIG,
  children,
}) => {
  const id = useId();
  const tooltipId = `tooltip-${id.replace(/:/g, "")}`;
  const isObject = typeof text === "object";
  const isInteractive = isObject && trigger === "hover";
  const container = typeof document === "undefined" ? undefined : document.body;

  const triggerRef = useRef(null);
  const hideTimeout = useRef(null);
  const [show, setShow] = useState(false);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(hideTimeout.current);
    setShow(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hideTimeout.current = setTimeout(() => setShow(false), HIDE_DELAY_MS);
  }, []);

  useEffect(() => () => clearTimeout(hideTimeout.current), []);

  if (!text) {
    return <span className={className}>{children}</span>;
  }

  const content = isObject ? (
    <div className={tooltipContentClassName}>{text}</div>
  ) : (
    <div
      className={tooltipContentClassName}
      dangerouslySetInnerHTML={{ __html: text }}
    />
  );

  if (isInteractive) {
    return (
      <>
        <span
          ref={triggerRef}
          className={className}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleMouseEnter}
          onBlur={handleMouseLeave}
        >
          {children}
        </span>
        <Overlay
          target={triggerRef.current}
          show={show}
          placement={placement}
          flip={flip}
          container={container}
          popperConfig={popperConfig}
        >
          <Tooltip
            id={tooltipId}
            className={innerClassName || undefined}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {content}
          </Tooltip>
        </Overlay>
      </>
    );
  }

  const rbTrigger = trigger === "hover" ? ["hover", "focus"] : trigger;

  return (
    <OverlayTrigger
      trigger={rbTrigger}
      placement={placement}
      flip={flip}
      container={container}
      popperConfig={popperConfig}
      overlay={
        <Tooltip id={tooltipId} className={innerClassName || undefined}>
          {content}
        </Tooltip>
      }
      rootClose={trigger === "click"}
    >
      <span className={className}>{children}</span>
    </OverlayTrigger>
  );
};

Tooltipable.propTypes = {
  trigger: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placement: PropTypes.string,
  className: PropTypes.string,
  tooltipContentClassName: PropTypes.string,
  innerClassName: PropTypes.string,
  flip: PropTypes.bool,
  popperConfig: PropTypes.object,
  children: PropTypes.node,
};

export default Tooltipable;
